import { useCallback, useLayoutEffect, useState } from 'react'

const BREAKPOINT_MAP = {
  '2xs': '24rem',
  xs: '32rem',
  sm: '40rem',
  md: '48rem',
  lg: '64rem',
  xl: '80rem',
  '2xl': '96rem',
}
const BREAKPOINTS = Object.keys(BREAKPOINT_MAP)

type Breakpoint = keyof typeof BREAKPOINT_MAP

function getMediaQuery (breakpoint: Breakpoint) {
  const query = BREAKPOINT_MAP[breakpoint]

  return window.matchMedia(`(min-width: ${query})`)
}

function getBreakpoint () {
  const match = [...BREAKPOINTS].reverse().find(
    (breakpoint): breakpoint is Breakpoint => getMediaQuery(breakpoint as Breakpoint).matches
  )

  return match || null
}

export function useBreakpoint () {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint | null>(getBreakpoint)

  useLayoutEffect(() => {
    function handleChange () {
      setCurrentBreakpoint(getBreakpoint())
    }

    const mediaQueries = BREAKPOINTS.map((breakpoint) => (
      getMediaQuery(breakpoint as Breakpoint)
    ))

    mediaQueries.forEach((mediaQuery) => {
      mediaQuery.addEventListener('change', handleChange)
    })

    return () => {
      mediaQueries.forEach((mediaQuery) => {
        mediaQuery.removeEventListener('change', handleChange)
      })
    }
  }, [])

  const breakpointIsAtLeast = useCallback((breakpoint: Breakpoint) => {
    const currentBreakpointIndex = BREAKPOINTS.indexOf(currentBreakpoint || '')
    const breakpointIndex = BREAKPOINTS.indexOf(breakpoint)

    return currentBreakpointIndex >= breakpointIndex
  }, [currentBreakpoint])

  const breakpointIsAtMost = useCallback((breakpoint: Breakpoint) => {
    const currentBreakpointIndex = BREAKPOINTS.indexOf(currentBreakpoint || '')
    const breakpointIndex = BREAKPOINTS.indexOf(breakpoint)

    return currentBreakpointIndex <= breakpointIndex
  }, [currentBreakpoint])

  const breakpointIsBetween = useCallback((min: Breakpoint, max: Breakpoint) => {
    const currentBreakpointIndex = BREAKPOINTS.indexOf(currentBreakpoint || '')
    const minIndex = BREAKPOINTS.indexOf(min)
    const maxIndex = BREAKPOINTS.indexOf(max)

    return currentBreakpointIndex >= minIndex && currentBreakpointIndex <= maxIndex
  }, [currentBreakpoint])

  return {
    breakpoint: currentBreakpoint,
    breakpointIsAtLeast,
    breakpointIsAtMost,
    breakpointIsBetween,
  }
}
